/**
 * LA impersonation — client-side session storage helpers.
 *
 * Design spec: .claude/design/impersonation.md ("UI affordance" + "What actually shipped").
 * Backend contract (unchanged by this file):
 *   POST /api/v1/impersonation/start -> { impersonationId, expiresAt, effectiveRole, effectiveUserId, projectId }
 *   POST /api/v1/impersonation/:id/end
 *   Every subsequent request while a session is active attaches `X-Impersonation-Id` so the
 *   backend's `resolveEffectiveActor` middleware can resolve the effective actor (Commandment III/VI —
 *   tenant isolation + auditability still apply to the effective role, not the raw LA token).
 *
 * Two sessionStorage keys are used deliberately:
 *   - SESSION_ID_KEY  ('impersonationSessionId') — just the id, cheap to check on every render/request.
 *   - SESSION_BLOB_KEY ('impersonationSession')  — the full JSON blob (id + expiry + display fields)
 *     the banner needs to render without re-fetching anything. Written together by
 *     ImpersonationDialog on a successful start; cleared together on end/expiry.
 *
 * NOTE (updated for R026/R027 — .claude/design/approval-matrix-clarification.md): as of
 * `AppContext.jsx::saveScopingProfile`'s opt-in `{ impersonated: true }` third argument,
 * `impersonationHeaders()` DOES have live callers — but only where a caller explicitly opts in
 * (today: the two "Act on behalf of Company SPOC" completion actions in
 * `OrganizationalSetup.jsx`). Every other `saveScopingProfile`/`persistWizard` call site, and
 * every other fetch in the app, still does not spread this header — rolling `resolveEffectiveActor`
 * out route-by-route beyond these two remains explicit follow-up work. Before citing "wired" or
 * "not wired" for any specific call site, grep that site directly rather than trusting this note's
 * age — see lesson [[doc-comment-directive-outlives-its-scope-claim]].
 */

const SESSION_ID_KEY = 'impersonationSessionId';
const SESSION_BLOB_KEY = 'impersonationSession';

/**
 * @returns {{ 'X-Impersonation-Id': string } | {}} Header fragment to spread into a fetch's
 * `headers` object. Empty object when no impersonation session is active, so `{...impersonationHeaders()}`
 * is always safe to spread unconditionally.
 */
export function impersonationHeaders() {
    let id = null;
    try {
        id = sessionStorage.getItem(SESSION_ID_KEY);
    } catch {
        /* sessionStorage unavailable (privacy mode, SSR, etc.) — behave as "no session" */
        id = null;
    }
    return id ? { 'X-Impersonation-Id': id } : {};
}

/**
 * Reads and validates the stored impersonation session blob.
 * @returns {null | { impersonationId: string, expiresAt: string, effectiveRole: string,
 *   effectiveUserId: string|null, projectId: string, effectiveUserLabel?: string, projectName?: string }}
 * `null` when nothing is stored, the blob is malformed, or `expiresAt` has already passed —
 * callers should treat all three the same way (no active session to show/act on).
 */
export function getActiveImpersonation() {
    let raw = null;
    try {
        raw = sessionStorage.getItem(SESSION_BLOB_KEY);
    } catch {
        return null;
    }
    if (!raw) return null;

    let parsed;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return null;
    }

    if (!parsed || typeof parsed !== 'object' || !parsed.impersonationId || !parsed.expiresAt) {
        return null;
    }

    const expiresAtMs = new Date(parsed.expiresAt).getTime();
    if (Number.isNaN(expiresAtMs) || expiresAtMs <= Date.now()) {
        return null;
    }

    return parsed;
}

/**
 * Persists a freshly-started impersonation session. Called by ImpersonationDialog on a
 * successful POST /impersonation/start response. Writes both storage keys together so
 * `impersonationHeaders()` and `getActiveImpersonation()` never see one without the other.
 * @param {{ impersonationId: string, expiresAt: string, effectiveRole: string,
 *   effectiveUserId?: string|null, projectId: string, effectiveUserLabel?: string, projectName?: string }} session
 */
export function setActiveImpersonation(session) {
    if (!session || !session.impersonationId) return;
    try {
        sessionStorage.setItem(SESSION_ID_KEY, session.impersonationId);
        sessionStorage.setItem(SESSION_BLOB_KEY, JSON.stringify(session));
    } catch {
        /* best-effort — if storage is unavailable the banner simply won't show */
    }
}

/** Clears both storage keys. Called on "End now", after a successful /end call, and on auto-expiry. */
export function clearActiveImpersonation() {
    try {
        sessionStorage.removeItem(SESSION_ID_KEY);
        sessionStorage.removeItem(SESSION_BLOB_KEY);
    } catch {
        /* ignore */
    }
}
